import { useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Paperclip, X } from 'lucide-react';

import { downloadBlob } from '@/lib/downloadFile';
import { notifyApiError } from '@/lib/notifyApiError';
import { useUploadDocument } from '@/features/patient-record/hooks/useDocumentMutations';
import { useConsentMutations } from '@/features/patient-record/hooks/useConsentMutations';
import { patientQueries } from '@/features/patient-record/queries';
import type {
  ConsentViewModel,
  DocumentViewModel,
} from '@/features/patient-record/types';

import { AttachmentPreview } from '../DocumentsSection/AttachmentPreview';
import { buildAttachmentDownloadUrl } from '../DocumentsSection/attachmentUrl';
import { isPreviewable } from '../DocumentsSection/previewable';
import { RowActionButton } from '../_shared/RowActions';

interface ConsentAttachmentButtonProps {
  readonly guid: string;
  readonly consent: ConsentViewModel;
}

/**
 * Attach affordance on a SIGNED consent row (PTV2-1210 / S3).
 *
 *  - No attachment yet   → paperclip button opens the native file picker;
 *                          upload goes through the shared person-attachment
 *                          endpoint so it also appears in Documents, then
 *                          the consent's `documentRef` is PATCHed with the
 *                          returned attachment id.
 *  - Attachment linked   → clickable filename chip: previewable files open
 *                          the shared AttachmentPreview modal, others
 *                          download directly. Small X unlinks — clears
 *                          `documentRef` only, the underlying attachment
 *                          record stays in Documents.
 */
export function ConsentAttachmentButton({
  guid,
  consent,
}: ConsentAttachmentButtonProps): ReactElement {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadDocument(guid);
  const { setDocumentRef } = useConsentMutations(guid);
  const [previewing, setPreviewing] = useState<DocumentViewModel | null>(null);

  // Reads the documents cache so we can show the linked attachment's
  // filename and decide whether it's previewable.
  const documentsQuery = useQuery(patientQueries.documents(guid));
  const linkedDocument =
    consent.documentRef === null
      ? undefined
      : documentsQuery.data?.find((d) => d.id === consent.documentRef);

  const attachPending = upload.isPending || setDocumentRef.isPending;

  function handlePickerChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    // Clear the input immediately so re-picking the same file re-fires change.
    event.target.value = '';
    if (file === undefined) return;
    upload.mutate(
      {
        title: t('patient.consent.attachment.uploadTitle', { name: consent.consentType }),
        documentType: 'DOCUMENT',
        file,
      },
      {
        onSuccess: (created) => {
          setDocumentRef.mutate({ id: consent.id, documentRef: created.id });
        },
      },
    );
  }

  if (consent.documentRef === null) {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handlePickerChange}
          aria-hidden="true"
        />
        <RowActionButton
          icon={<Paperclip className="h-3.5 w-3.5" aria-hidden="true" />}
          label={t('patient.consent.attachment.attach')}
          onClick={() => {
            fileInputRef.current?.click();
          }}
          disabled={attachPending}
        />
      </>
    );
  }

  const label =
    linkedDocument?.fileName ?? linkedDocument?.title ?? t('patient.consent.attachment.linked');
  const previewable =
    linkedDocument !== undefined && isPreviewable(linkedDocument.fileName);

  // Click semantics: preview when we can, download otherwise. Falls back
  // to a plain download URL for the linked GUID even when the documents
  // cache hasn't populated yet (so the chip is never a dead link).
  function handleOpen(): void {
    if (linkedDocument !== undefined && previewable) {
      setPreviewing(linkedDocument);
      return;
    }
    const filename =
      linkedDocument?.fileName ?? linkedDocument?.title ?? consent.consentType;
    downloadBlob(buildAttachmentDownloadUrl(consent.documentRef ?? ''), filename).catch(
      notifyApiError,
    );
  }

  const actionTitle = previewable
    ? t('patient.consent.attachment.view')
    : t('patient.consent.attachment.download');

  return (
    <>
      <span className="inline-flex items-center gap-1 rounded border border-border/60 bg-muted/40 pl-1.5 pr-0.5 py-0.5 text-xs text-muted-foreground max-w-[10rem]">
        <button
          type="button"
          onClick={handleOpen}
          title={actionTitle}
          aria-label={actionTitle}
          className="inline-flex min-w-0 items-center gap-1 truncate text-foreground/80 hover:text-foreground hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded"
        >
          <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">{label}</span>
        </button>
        <button
          type="button"
          onClick={() => {
            setDocumentRef.mutate({ id: consent.id, documentRef: null });
          }}
          disabled={setDocumentRef.isPending}
          aria-label={t('patient.consent.attachment.unlinkAria', {
            name: consent.consentType,
          })}
          title={t('patient.consent.attachment.unlink')}
          className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-40"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </span>
      {previewing !== null && (
        <AttachmentPreview
          document={previewing}
          downloadUrl={buildAttachmentDownloadUrl(previewing.id)}
          onClose={() => {
            setPreviewing(null);
          }}
        />
      )}
    </>
  );
}
